import { useEffect } from 'react';
import { endpoints } from '../services/api';
import { assignmentFor, useScreen } from '../store/screen';
import { useSession } from '../store/session';
import { LAYOUTS, useSettings } from '../store/settings';

/**
 * While signed in, this screen checks in every 90 seconds (and when it comes back into view)
 * so it appears in Settings → Screens with what it's showing; the answer says what the
 * account has chosen for it – a layout or rotation, and its schedule – which
 * hooks/useScreenPlan.ts then puts into effect.
 */
export function useScreenHeartbeat(): void {
  const userId = useSession((s) => s.user?.id ?? null);
  const accounts = useSession((s) => s.config?.features.accounts ?? false);

  useEffect(() => {
    if (!userId || !accounts) return;
    let cancelled = false;
    const beat = async () => {
      const s = useSettings.getState();
      const screen = useScreen.getState();
      const assigned = assignmentFor(userId);
      const preset = assigned ? s.presets.find((p) => p.id === assigned) : undefined;
      const layout = LAYOUTS.find((l) => l.id === s.layout)?.name;
      const rotating = (screen.plans[userId]?.rotation?.items.length ?? 0) > 1;
      const shown = `${rotating ? 'Rotating: ' : ''}${preset ? preset.name : assigned?.startsWith('shared:') ? 'Shared canvas' : 'Main setup'}${layout ? ` · ${layout}` : ''}`;
      const dpr = window.devicePixelRatio || 1;
      try {
        const { device, schedule } = await endpoints.screenHeartbeat({
          deviceId: screen.deviceId,
          screen: { width: Math.round(window.screen.width * dpr), height: Math.round(window.screen.height * dpr) },
          showing: (screen.off ? 'Off (schedule)' : shown).slice(0, 80),
        });
        if (!cancelled) useScreen.getState().setPlan(userId, { presetId: device.presetId, rotation: device.rotation, schedule });
      } catch {
        /* offline or signed out: try again next time */
      }
    };
    void beat();
    const timer = window.setInterval(() => void beat(), 90_000);
    const onVisible = () => document.visibilityState === 'visible' && void beat();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [userId, accounts]);
}
